import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { useControl } from 'react-map-gl/mapbox';
import { ControlPosition } from 'mapbox-gl';

const DRAW_EVENTS = {
  'draw.actionable': 'onDrawActionable',
  'draw.combine': 'onDrawCombine',
  'draw.create': 'onDrawCreate',
  'draw.delete': 'onDrawDelete',
  'draw.modechange': 'onDrawModeChange',
  'draw.render': 'onDrawRender',
  'draw.selectionchange': 'onDrawSelectionChange',
  'draw.uncombine': 'onDrawUncombine',
  'draw.update': 'onDrawUpdate'
} as const;
type DrawEventName = keyof typeof DRAW_EVENTS;

interface DrawEventHandlers {
  onDrawActionable?: (event: MapboxDraw.DrawActionableEvent) => void;
  onDrawCombine?: (event: MapboxDraw.DrawCombineEvent) => void;
  onDrawCreate?: (event: MapboxDraw.DrawCreateEvent) => void;
  onDrawDelete?: (event: MapboxDraw.DrawDeleteEvent) => void;
  onDrawModeChange?: (event: MapboxDraw.DrawModeChangeEvent) => void;
  onDrawRender?: (event: MapboxDraw.DrawRenderEvent) => void;
  onDrawSelectionChange?: (event: MapboxDraw.DrawSelectionChangeEvent) => void;
  onDrawUncombine?: (event: MapboxDraw.DrawUncombineEvent) => void;
  onDrawUpdate?: (event: MapboxDraw.DrawUpdateEvent) => void;
}

export interface DrawControlProps
  extends MapboxDraw.MapboxDrawOptions,
    DrawEventHandlers {
  position?: ControlPosition;
  /** The control is on the map and its store exists; `draw.*` calls are safe. */
  onReady?: () => void;
  /** The control is about to leave the map. */
  onRemoved?: () => void;
}

export interface DrawControlHandle {
  draw: MapboxDraw;
}

// mapbox-gl's typed `on`/`off` only know the map's own event names; draw
// events are custom strings fired on the same emitter.
interface DrawEventTarget {
  on(type: string, listener: (event: unknown) => void): unknown;
  off(type: string, listener: (event: unknown) => void): unknown;
}

export const DrawControl = forwardRef<DrawControlHandle, DrawControlProps>(
  (props, ref) => {
    const {
      position = 'top-left',
      modes,
      onDrawActionable,
      onDrawCombine,
      onDrawCreate,
      onDrawDelete,
      onDrawModeChange,
      onDrawRender,
      onDrawSelectionChange,
      onDrawUncombine,
      onDrawUpdate,
      onReady,
      onRemoved,
      ...drawOptions
    } = props;

    const latestProps = useRef(props);
    latestProps.current = props;

    // Bound once when the control is added; each dispatches to whatever
    // handler the latest render passed. State, not a memo: off() must get
    // the very same functions on() was given.
    const [listeners] = useState(() =>
      (Object.keys(DRAW_EVENTS) as DrawEventName[]).map(event => ({
        event,
        listener: (payload: unknown) => {
          const handler = latestProps.current[DRAW_EVENTS[event]] as
            | ((event: unknown) => void)
            | undefined;
          handler?.(payload);
        }
      }))
    );

    const draw = useControl<MapboxDraw>(
      () =>
        new MapboxDraw({
          ...drawOptions,
          modes: { ...MapboxDraw.modes, ...modes }
        }),
      // Readiness is published from here, not from the ref: the ref lands in
      // the commit before this effect attaches the control to the map, and a
      // draw call on an unattached control has no store to work on.
      ({ map }) => {
        const target = (map.getMap() as unknown) as DrawEventTarget;
        listeners.forEach(({ event, listener }) => target.on(event, listener));
        latestProps.current.onReady?.();
      },
      ({ map }) => {
        const target = (map.getMap() as unknown) as DrawEventTarget;
        listeners.forEach(({ event, listener }) => target.off(event, listener));
        latestProps.current.onRemoved?.();
      },
      { position }
    );

    useImperativeHandle(ref, () => ({ draw }), [draw]);

    return null;
  }
);

DrawControl.displayName = 'DrawControl';
